import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'ah-theme';

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function resolveInitialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage unavailable
  }
  return getSystemTheme();
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// ── Shared external store ─────────────────────────────────────────────────────
// A single source of truth so every useTheme() consumer (ThemeProvider,
// ThemeToggle, …) shares one state and re-renders together on toggle.
let currentTheme = resolveInitialTheme();
const listeners = new Set();

function setThemeStore(next) {
  if (next !== 'dark' && next !== 'light') return;
  if (next === currentTheme) return;
  currentTheme = next;
  applyTheme(currentTheme);
  try {
    localStorage.setItem(STORAGE_KEY, currentTheme);
  } catch {
    // localStorage unavailable
  }
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return currentTheme;
}

// Apply the resolved theme to <html> as early as this module loads (belt-and-
// braces alongside the pre-paint script in index.html).
applyTheme(currentTheme);

// Follow OS preference changes only while the user has made no explicit choice.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    if (stored !== 'dark' && stored !== 'light') {
      setThemeStore(getSystemTheme());
    }
  });
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setTheme = useCallback((next) => setThemeStore(next), []);
  const toggle = useCallback(
    () => setThemeStore(currentTheme === 'dark' ? 'light' : 'dark'),
    []
  );

  return { theme, setTheme, toggle };
}
